import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { ProductListItem } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { productTypesQueryOptions } from '@/features/dictionary';
import { session } from '@/shared/session/session';

import { ProductStatusBadge } from './product-status-badge';
import { ProductThumb } from './product-thumb';

/** Past this many stores a row of dots stops being readable; "3 of 20" says it alone. */
const MAX_STORE_DOTS = 6;

/** Column model of the products table; `meta` ties columns to the list's sorts and filters. */
export function useProductColumns() {
  const { t } = useTranslation('products');
  const format = useFormat();
  const { me } = session.useSession();
  const { data: productTypes } = useQuery(productTypesQueryOptions());

  const typeNames = useMemo(
    () => new Map((productTypes ?? []).map((type) => [type.id, type.name])),
    [productTypes],
  );
  const stores = useMemo(() => me?.stores ?? [], [me?.stores]);

  return useMemo<Array<DataTableColumnDef<ProductListItem>>>(
    () => [
      {
        id: 'title',
        accessorKey: 'title',
        header: t('columns.title'),
        cell: ({ row }) => {
          const type = row.original.productTypeId
            ? typeNames.get(row.original.productTypeId)
            : undefined;
          const subtitle = [type, row.original.vendor].filter(Boolean).join(' · ');
          return (
            <div className="flex min-w-0 items-center gap-3">
              <ProductThumb image={row.original.featuredImage} />
              <div className="max-w-[300px] min-w-0">
                <Link
                  to="/org/products/$productId"
                  params={{ productId: row.original.id }}
                  className="block truncate font-medium hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {row.original.title}
                </Link>
                {subtitle ? (
                  <span className="text-muted-foreground block truncate text-xs">{subtitle}</span>
                ) : null}
              </div>
            </div>
          );
        },
        meta: { sortField: 'title', lockVisibility: true },
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('columns.status'),
        cell: ({ row }) => <ProductStatusBadge status={row.original.status} />,
        meta: { filterKey: 'status', tour: 'catalog.products.status-badge' },
        size: 100,
      },
      {
        id: 'stores',
        header: t('columns.stores'),
        cell: ({ row }) => {
          const holding = stores.filter((store) => row.original.storeIds.includes(store.id));
          return (
            <span
              className="inline-flex items-center gap-2"
              title={
                holding.length > 0
                  ? holding.map((store) => store.name).join(', ')
                  : t('list.storesNoneTitle')
              }
            >
              {stores.length <= MAX_STORE_DOTS ? (
                <span aria-hidden className="inline-flex gap-0.5">
                  {stores.map((store, index) => (
                    <span
                      key={store.id}
                      className={cn(
                        'size-2 rounded-full',
                        index < holding.length ? 'bg-primary' : 'bg-border',
                      )}
                    />
                  ))}
                </span>
              ) : null}
              <span className={cn(holding.length === 0 && 'text-destructive')}>
                {holding.length === 0
                  ? t('list.storesNone')
                  : t('list.storesOf', { count: holding.length, total: stores.length })}
              </span>
            </span>
          );
        },
        meta: { filterKey: 'inStoreId' },
        size: 120,
      },
      {
        id: 'inventory',
        header: t('columns.inventory'),
        cell: ({ row }) => {
          const { stock, fulfillmentMode, variantCount } = row.original;
          if (!stock) {
            return (
              <span className="text-muted-foreground">
                {t(fulfillmentMode === 'made_to_order' ? 'list.madeToOrder' : 'list.notTracked')}
              </span>
            );
          }
          return (
            <span>
              <span
                className={cn(
                  stock.available <= 0 && 'text-destructive',
                  stock.available > 0 && stock.low && 'bg-warning/25 text-foreground rounded px-1',
                )}
              >
                {t('list.stock', { available: stock.available })}
              </span>
              <span className="text-muted-foreground">
                {' · '}
                {t('list.variantCount', { count: variantCount })}
              </span>
            </span>
          );
        },
        meta: { filterKey: 'stock' },
        size: 190,
      },
      {
        id: 'price',
        header: t('columns.price'),
        cell: ({ row }) => {
          const range = row.original.priceRange;
          if (!range) return <span className="text-muted-foreground">{t('list.noPrice')}</span>;
          return range.min.amount === range.max.amount
            ? format.money(range.min)
            : `${format.money(range.min)} – ${format.money(range.max)}`;
        },
        meta: { sortField: 'price', align: 'end' },
        size: 150,
      },
      {
        id: 'updatedAt',
        accessorKey: 'updatedAt',
        header: t('columns.updatedAt'),
        cell: ({ row }) => (
          <span className="text-muted-foreground" title={format.utc(row.original.updatedAt)}>
            {format.relative(row.original.updatedAt)}
          </span>
        ),
        meta: { sortField: 'updatedAt', filterKey: 'updatedAt' },
        size: 120,
      },
    ],
    [format, stores, t, typeNames],
  );
}
